import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Component to display tenant information
 * Used to demonstrate the multi-tenant functionality
 */
const TenantInfo: React.FC = () => {
  const { t } = useTranslation();
  const { tenant, loading, error } = useTenant();
  const { theme } = useTheme();
  
  if (loading) {
    return (
      <div className={`p-4 rounded-lg shadow-sm ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        <p className={`text-sm ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}>
          Loading tenant information...
        </p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`p-4 rounded-lg shadow-sm ${
        theme === 'dark' ? 'bg-gray-800 border-red-800' : 'bg-white border-red-200'
      } border`}>
        <p className={`text-sm ${
          theme === 'dark' ? 'text-red-400' : 'text-red-600'
        }`}>
          {error}
        </p>
      </div>
    );
  }
  
  if (!tenant) {
    return (
      <div className={`p-4 rounded-lg shadow-sm ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        <p className={`text-sm ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}>
          You are on the main FlowCXP domain.
        </p>
        <p className={`text-sm mt-2 ${
          theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
        }`}>
          <a href="/register">Register your organization</a> to get started.
        </p>
      </div>
    );
  }
  
  return (
    <div className={`p-4 rounded-lg shadow-sm ${
      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
    }`}>
      <div className="flex items-center justify-between">
        <h3 className={`font-medium ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          {t('tenantInfo.organizationInformation')}
        </h3>
        <span className={`px-2 py-1 text-xs rounded-full ${
          theme === 'dark' 
            ? 'bg-green-900/30 text-green-400' 
            : 'bg-green-100 text-green-800'
        }`}>
          {t('tenantInfo.active')}
        </span>
      </div>
      
      <div className="mt-4 grid grid-cols-3 gap-4">
        <div>
          <p className={`text-xs ${
            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {t('tenantInfo.organizationName')}
          </p>
          <p className={`text-sm font-medium ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            {tenant.name}
          </p>
        </div>
        <div>
          <p className={`text-xs ${
            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {t('tenantInfo.subdomain')}
          </p>
          <p className={`text-sm font-medium ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            {tenant.subdomain}
          </p>
        </div>
        <div>
          <p className={`text-xs ${
            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {t('tenantInfo.tenantId')}
          </p>
          <p className={`text-sm font-mono ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {tenant.id}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TenantInfo;
